/**
 * ASN detail modal footer — workflow actions, exports and print.
 *
 * Extracted from `ASNDetailModal` (CR-7194) to keep that file under the 500-line
 * cap. Presentational: every decision is passed in, so the rules about who may
 * do what stay visible in one place here rather than being spread through the
 * modal's JSX.
 *
 * The role gates are not cosmetic — each one mirrors a server-side permission,
 * and a button rendered without its entitlement would fail on click.
 */
import { Send, CheckCircle, Download, Printer, Loader2 } from 'lucide-react';
import type { ASNData } from './asnModalTypes';
import { downloadProdumaxCsv } from './asnProdumaxExport';
import { downloadAsnCsv } from './asnConfigurableExport';

interface ASNDetailFooterProps {
  asnData: ASNData;
  /** A workflow request is in flight — disables the actions and Close. */
  loading: boolean;
  /** The PDF is being generated. */
  printing: boolean;
  /** True when the viewer is on the supplier side. */
  isSupplierUser: boolean;
  /** Whether the tenant's Produmax warehouse export is switched on. */
  produmaxExportEnabled: boolean;
  onWorkflowAction: (action: string) => void;
  onPrint: () => void;
  onClose: () => void;
}

const ACTION_BUTTON =
  'px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2';
const NEUTRAL_BUTTON = `${ACTION_BUTTON} bg-slate-600 hover:bg-slate-700`;

export function ASNDetailFooter({
  asnData, loading, printing, isSupplierUser, produmaxExportEnabled,
  onWorkflowAction, onPrint, onClose,
}: ASNDetailFooterProps) {
  return (
    <div className="flex justify-between items-center p-4 border-t">
      <div className="flex gap-3">
        {/* CR-4888 round 2 / Error 9: Submit to Customer is supplier-only.
            Was rendering for customers too whenever status='created'. */}
        {asnData.status === 'created' && isSupplierUser && (
          <button
            onClick={() => onWorkflowAction('submit')}
            disabled={loading}
            className={`${ACTION_BUTTON} bg-blue-600 hover:bg-blue-700`}
          >
            <Send className="h-4 w-4" />Submit to Customer
          </button>
        )}
        {/* CR-4888 round 2 / Error 10: Acknowledge is customer-only. Supplier
            was being shown the button but the API rejects it with "only the
            customer can acknowledge this ASN" — so the button violated its own
            permission contract.

            CR-7142: `status === 'submitted'` used to imply "not yet
            acknowledged", because acknowledging moved the ASN to in_transit. A
            direct load now stays at 'submitted' on purpose, so the
            acknowledgement date is what says whether the action is still
            available — without it the button lingered after a successful
            acknowledge and invited a second click. */}
        {asnData.status === 'submitted'
          && !asnData.customer_acknowledgment_date
          && !isSupplierUser && (
          <button
            onClick={() => onWorkflowAction('acknowledge')}
            disabled={loading}
            className={`${ACTION_BUTTON} bg-green-600 hover:bg-green-700`}
          >
            <CheckCircle className="h-4 w-4" />
            {/* CR-7142: "(In Transit)" is a promise this button does not keep
                for a wholly-direct load — the stock goes straight to the
                customer, so acknowledging records that it left the supplier
                without moving it to In Transit. A mixed load still travels to
                the warehouse, so it keeps the suffix. */}
            {asnData.is_direct_delivery ? 'Acknowledge' : 'Acknowledge (In Transit)'}
          </button>
        )}
        {/* CR-4888 rework (Error 10b): "Mark as Delivered" removed from the ASN
            footer for ALL roles — delivery is set implicitly when the customer
            books a GRN against the shipment, per QA. The 'mark_delivered'
            handler + markASNDelivered API are intentionally left in place so
            the manual-mark workflow can be reinstated by re-adding a button
            here that calls onWorkflowAction('mark_delivered'). */}
        {produmaxExportEnabled && (
          <button onClick={() => downloadProdumaxCsv(asnData)} className={NEUTRAL_BUTTON}>
            <Download className="h-4 w-4" />
            Produmax Export
          </button>
        )}
        {/* CR-7142 / CR-7194: the ASN data export. Started life as a
            direct-delivery-only SAP upload with a fixed five-column layout; it
            is now driven by the company's saved column format, so the same
            button serves a supplier reading the file by eye and one uploading
            it into an ERP (the latter configures the exact columns + header
            names — see the seed_asn_export_layout command). The direct-only
            restriction is gone with the fixed layout: this is a data extract of
            the whole ASN, not an instruction to clear a PO by hand, so
            warehouse lines belong in it too.

            This is a DIFFERENT thing from the Produmax warehouse export
            above, which keeps its own tenant flag. This one is not flagged at
            all — it extracts the grid the viewer is already looking at, and the
            server says whether the ASN has such a grid via
            `csv_export_enabled`. An earlier revision gated it on the Produmax
            flag; because the platform resolves that against the viewer's own
            company, and a supplier has no reason to carry the buying side's
            flag, it hid the export from every supplier. */}
        {asnData.csv_export_enabled && (
          <button onClick={() => downloadAsnCsv(asnData)} className={NEUTRAL_BUTTON}>
            <Download className="h-4 w-4" />
            Export
          </button>
        )}
        <button onClick={onPrint} disabled={printing} className={NEUTRAL_BUTTON}>
          {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          Print ASN
        </button>
      </div>
      <button
        onClick={onClose}
        disabled={loading}
        className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50"
      >
        Close
      </button>
    </div>
  );
}
